import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTION_TONE = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-red-50 text-red-700 border-red-200",
  correct: "bg-amber-50 text-amber-700 border-amber-200",
  login: "bg-neutral-100 text-neutral-700 border-neutral-200",
  logout: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function AuditPage() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (entity !== "all") params.entity = entity;
    if (action !== "all") params.action = action;
    const r = await api.get("/audit", { params });
    setRows(r.data);
  }, [entity, action]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "created_at", label: "Wanneer", render: r => (
      <div className="text-xs">
        <div>{new Date(r.created_at).toLocaleString('nl-NL')}</div>
      </div>
    )},
    { key: "user_email", label: "Wie", render: r => (
      <div>
        <div className="text-sm font-medium">{r.user_email}</div>
        <div className="text-xs text-neutral-500 capitalize">{r.user_role}</div>
      </div>
    )},
    { key: "action", label: "Actie", render: r => (
      <Badge variant="outline" className={`border ${ACTION_TONE[r.action] || ""}`}>{r.action}</Badge>
    )},
    { key: "entity", label: "Entiteit", render: r => (
      <div>
        <div className="text-sm">{r.entity}</div>
        {r.entity_id && <div className="text-[10px] text-neutral-400 font-mono truncate max-w-[140px]">{r.entity_id.slice(0, 8)}…</div>}
      </div>
    )},
    { key: "reason", label: "Reden", render: r => r.reason
      ? <span className="text-xs text-neutral-700">{r.reason}</span>
      : <span className="text-neutral-300 text-xs">—</span> },
    { key: "ip", label: "IP", render: r => <span className="font-mono text-[10px] text-neutral-500">{r.ip || "—"}</span> },
  ];

  return (
    <div>
      <PageHeader
        title="Logboek — Audit trail"
        subtitle="Onwisbaar overzicht van alle wijzigingen. Elke correctie of mutatie is hier terug te vinden."
        testid="audit-header"
        actions={
          <div className="flex items-center gap-2">
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="w-40" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Alle entiteiten</SelectItem>
                {["child", "parent", "contract", "invoice", "medewerker", "group", "location", "user", "presentie"].map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Alle acties</SelectItem>
                {["create", "update", "delete", "correct", "login", "logout"].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />
      <DataTable columns={columns} rows={rows} testid="audit-table" />
    </div>
  );
}
